import { useStoreHooks } from '@/store/hook'
import { reactive, computed, ref } from 'vue'
import { getVideoInfo, entroll, getDetail } from '@/request/api/course'
import { ICourseQueryCondition, IVideoQueryCondition } from '~/typings'
import {
  ICourseContentDTOS,
  IResponseVideoInfo,
  IResponseCourseList,
  IResponseCourseDetail,
  ICourseListItem
} from '@/interface/course'
import { IBannerListItem, IOptionItem, EnrollSalesInvitationDetail } from '@/interface'
import { useRoute } from 'vue-router'
import { useClientCompatibleHook, useEnrollModalHook } from '@/utils/hooks'
import { CERTIFICATION_STATUS } from '@/utils/moduleHooks/certification'
import { useI18n } from 'vue-i18n'
import Message from '@/components/common/message'
import { CERTIFICATION_TYPE, ENROLL_RELATION_TYPE } from '../config'
import { enrollSalesInvitation } from '@/request/api/invitation'
import { EMAIL_NOTIFICATION_STATUS } from '@/request/api/user'
import { TENANT_ID_MAP } from '@/utils'
interface CourseStateType {
  list: ICourseListItem[]
  total: number
  showFilter: boolean
  activeNames: string[]
  banner: IBannerListItem
}

export const COURSE_TYPE = {
  course: '0',
  video: '1'
}

export const RECOMMEND_COURSE_TYPE = {
  course: '1',
  video: '2'
}

export const QUIZ_STATUS = {
  success: '1',
  fail: '0'
}

const insertAllItem = (arr: IOptionItem[], name: string): IOptionItem[] => {
  const { t } = useI18n()
  return [
    { [name]: t('global.filter.all'), id: '' },
    ...arr
  ]
}

export const HANDS_ON_STATUS = {
  pending: '0',
  learning: '1',
  finish: '2'
}

export const LEARNING_PROGRESS = {
  notStart: '0',
  learning: '1',
  learnEnd: '2',
  success: '3',
  fail: '4'
}

export const LEARNING_STATUS = {
  continue: ['0', '1'],
  startExam: ['2', '3'],
  apply: ['4', '5'],
  passed: ['6']
}

export const RegistrationStatus = {
  // 未报名
  NotEntroll: '0',
  // 报名未审核通过
  NotAudited: '1',
  // 报名审核通过
  Audited: '2'
}

// 课程考试信息hook
export function useCourseExamHook () {
  const { state: _state, getters } = useStoreHooks()
  const { push } = useClientCompatibleHook()
  const examResult = computed(() => _state.exam.examResult)
  const state = reactive({
    quizVisible: false,
    isShowVideo: true,
    loginTipDialogVisible: false,
    quizStatus: ''
  })
  const isLogin = computed(() => getters['global/isLogin'])
  const legalStatus = [CERTIFICATION_STATUS.exam, CERTIFICATION_STATUS.apply, CERTIFICATION_STATUS.finish]
  const ifUserGetAllCredits = computed(() => legalStatus.some(s => examResult.value.status === s))
  const ifUserQuizFailed = computed(() => examResult.value.passStatus === QUIZ_STATUS.fail)
  const goNext = () => {
    if (ifUserGetAllCredits.value) {
      push(`/certification/detail/${_state.course.detail.courseDetail.relationId}#take-exam`)
    } else {
      push(`/certification/detail/${_state.course.detail.courseDetail.relationId}#course-list-position`)
    }
  }

  const onStartQuiz = () => {
    if (!isLogin.value) {
      state.loginTipDialogVisible = true
      return
    }
    state.quizVisible = true
  }
  return {
    examState: state,
    examResult,
    onStartQuiz,
    ifUserGetAllCredits,
    goNext,
    ifUserQuizFailed
  }
}

// 课程基本信息hook
export function useCourseDetailHook () {
  const store = useStoreHooks()
  const { state: _state, getters, commit } = useStoreHooks()
  const { t } = useI18n()
  const route = useRoute()
  const courseType = (route.query.courseType || '0') as string
  const courseDetail = computed(() => _state.course.detail.courseDetail)
  const exerciseResult = computed(() => _state.exam.exerciseResult)
  const { onEnroll: _onEnroll } = useEnrollModalHook()
  const state = reactive({
    isLogin: getters['global/isLogin'],
    tabActiveKey: '1',
    currentChapter: {} as ICourseContentDTOS,
    showPracticalPage: false,
    // 课程看完 && 实操练习通过
    examDisable: computed(() =>
      // courseDetail.value.learningProcess !== LEARNING_PROGRESS.learnEnd ||
      courseDetail.value.learningProcess === undefined ||
      courseDetail.value.learningProcess === LEARNING_PROGRESS.notStart ||
      courseDetail.value.learningProcess === LEARNING_PROGRESS.learning ||
      (exerciseResult.value.examId && exerciseResult.value.exerciseProcess !== HANDS_ON_STATUS.finish)
    ),
    // videoCoverImage: '',
    currentVideoInfo: {
      key: '',
      url: '',
      lastPlayTime: 0
    }
  })
  const currentLearningProcess = computed(() => {
    const isCourseType = route.query.courseType === COURSE_TYPE.course
    if (isCourseType && (courseDetail.value.registrationStatus === RegistrationStatus.NotEntroll || !state.isLogin)) {
      return -1
    } else if (courseDetail.value.learningProcess === undefined || courseDetail.value.learningProcess === LEARNING_PROGRESS.notStart) {
      // Enroll
      return 0
    } else if (courseDetail.value.learningProcess === LEARNING_PROGRESS.learning) {
      // Learning
      return 1
    } else if (exerciseResult.value.examId && exerciseResult.value.exerciseProcess !== HANDS_ON_STATUS.finish) {
      // Hands on
      return 2
    }
    return -1
  })
  const isNextButtonHidden = computed(() => {
    const videoList = courseDetail.value.courseContentReleaseDTOS || []
    const crtItemIndex = videoList.findIndex(i => state.currentChapter.id === i.id)
    return videoList.length === 1 || crtItemIndex === videoList.length - 1
  })
  const showLockIcon = computed(() => {
    if (courseType === COURSE_TYPE.course) {
      return courseDetail.value.registrationStatus === RegistrationStatus.NotEntroll || courseDetail.value.registrationStatus === RegistrationStatus.NotAudited
    }
    return !state.isLogin
  })

  // 更新视频信息
  const setVideoInfo = async () => {
    const userEnrolled = route.query.courseType === COURSE_TYPE.video || courseDetail.value.registrationStatus === RegistrationStatus.Audited
    if (state.isLogin && userEnrolled) {
      const res = await getVideoInfo(
        courseDetail.value.id + '',
        state.currentChapter.id + '',
        route.query.courseType as string || '0'
      )
      const videoInfo = res.data as IResponseVideoInfo
      if (res.data) {
        state.currentVideoInfo = {
          key: videoInfo.key,
          url: videoInfo.url,
          lastPlayTime: parseInt(videoInfo.payDuration)
        }
      }
    }
  }

  const enrollLoading = ref(false)
  const inEnrollDialogVisible = ref(false)
  const inviteError = ref('')
  const enrollSuccessCallback = async (isInvite: boolean) => {
    await updateCourseDetail()
    setVideoInfo()

    if (isInvite) {
      Message.success(t('invitation.success'))
    } else {
      Message.success(t('global.enrollSubmitSuccess'))
    }
    inEnrollDialogVisible.value = false
  }

  const enrollCourse = (mailSubscriptionStatus?: boolean, courseType = '0') => {
    const uncheckedStatus = _state.global.userInfo?.emailNotificationStatus === EMAIL_NOTIFICATION_STATUS.subscribed
      ? EMAIL_NOTIFICATION_STATUS.cancelSubscribe : EMAIL_NOTIFICATION_STATUS.notSubscribe
    const emailNotificationStatus = mailSubscriptionStatus ? EMAIL_NOTIFICATION_STATUS.subscribed : uncheckedStatus
    if (enrollLoading.value) return
    enrollLoading.value = true
    let id = '-1'
    if (courseType === '0') {
      id = courseDetail.value.relationId
    } else {
      id = courseDetail.value.id + ''
    }
    entroll(
      id,
      emailNotificationStatus as string,
      courseType
    ).then(result => {
      if (result.code === 200) {
        enrollSuccessCallback(false)
        store.commit('global/setUserInfo', {
          ..._state.global.userInfo,
          emailNotificationStatus
        })
      }
    }).finally(() => { enrollLoading.value = false })
  }

  const confirmEnrollByInvite = (data: EnrollSalesInvitationDetail) => {
    if (!data.code) {
      inviteError.value = t('classroom.inputInvitatioinCode')
      return
    }
    if (enrollLoading.value) return
    enrollLoading.value = true
    data.relationId = courseDetail.value.relationId
    data.relationType = ENROLL_RELATION_TYPE.certification
    const emailNotificationStatus = data.emailNotificationStatus
    enrollSalesInvitation(data).then(result => {
      if (result?.code === 200) {
        enrollSuccessCallback(true)
      } else if (result && result.code === 10018) {
        const errorContext = result.errorContext || {}
        // 特殊动态文案
        inviteError.value = t('error.10018', { site: errorContext.codeValidWithSite || '' })
      } else if (result && result.code === 10019) {
        const errorContext = result.errorContext || {}
        // 特殊动态文案
        inviteError.value = t('error.10019', { country: errorContext.codeValidWithCountry || '' })
        store.commit('global/setUserInfo', {
          ..._state.global.userInfo,
          emailNotificationStatus
        })
      } else if (result?.message) {
        inviteError.value = t(`error.${result?.code}`)
      }
    }).finally(() => { enrollLoading.value = false })
  }

  // 报名课程
  const onEnroll = () => {
    _onEnroll(() => {
      if (courseDetail.value.kind === CERTIFICATION_TYPE.hsc) {
        if (getters['global/shouldUserSupplyInfo']) {
          commit('global/setGatherInfoVisible', true)
        }
        inEnrollDialogVisible.value = true
      } else if (_state.global.tenantId === TENANT_ID_MAP.micro && getters['global/shouldUserSupplyInfo']) {
        commit('global/setMicroEnrollCourse', true)
        commit('global/setGatherInfoVisible', true)
      } else {
        enrollCourse(undefined, courseType)
      }
    }, courseDetail.value.kind)
  }

  // 更新课程状态
  const updateCourseDetail = async () => {
    const result = await getDetail(route.params.id as string, route.query.courseType as string)
    const detail = (result as IResponseCourseDetail).courseDetail
    await commit('course/updateCourseDetail', detail)
  }

  // 更换章节
  const onChangeChapter = async (item: ICourseContentDTOS) => {
    await updateCourseDetail()
    state.currentChapter = item
    await setVideoInfo()
    // console.log('onChangeChapter', state.currentChapter, item)
  }

  // 看下一节视频
  const onPlayNextVideo = async () => {
    const videoList = courseDetail.value.courseContentReleaseDTOS || []
    const crtItemIndex = videoList.findIndex(i => state.currentChapter.id === i.id)
    if (crtItemIndex > -1 && videoList[crtItemIndex + 1]) {
      const nextItem = videoList[crtItemIndex + 1]
      onChangeChapter(nextItem)
    }
  }

  return {
    courseDetail,
    setVideoInfo,
    onEnroll,
    updateCourseDetail,
    courseState: state,
    onChangeChapter,
    onPlayNextVideo,
    isNextButtonHidden,
    inEnrollDialogVisible,
    enrollCourse,
    confirmEnrollByInvite,
    inviteError,
    currentLearningProcess,
    showLockIcon
  }
}

export function createDefaultCondition (isMobile?: boolean): ICourseQueryCondition {
  return {
    keyword: '',
    certificationSubtypeName: '',
    certificationTypeName: '',
    requirement: '',
    pageNum: 1,
    pageSize: isMobile ? 10 : 15
  }
}

export function createDefaultVideoCondition (isMobile?: boolean): IVideoQueryCondition {
  return {
    keyword: '',
    productLineId: '',
    videoTypeId: '',
    type: '0',
    pageNum: 1,
    pageSize: isMobile ? 10 : 15
  }
}

export function createListReactiveState () {
  const state = reactive({
    list: [] as ICourseListItem[],
    total: 0,
    showFilter: false,
    activeNames: ['certificationType'],
    banner: {} as IBannerListItem
  }) as CourseStateType
  return state
}

export function setListReactiveState (state: CourseStateType, listData: IResponseCourseList) {
  state.total = listData.courseList.total
  state.list = listData.courseList.rows
  state.banner = listData.banner || {}
}

export function createCourseConditionReactive (isMobile: boolean) {
  return reactive({
    ...createDefaultCondition(isMobile)
  })
}

export function createVideoConditionReactive (isMobile: boolean) {
  return reactive({
    ...createDefaultVideoCondition(isMobile)
  })
}

export function setCourseConditionReactive (condition: ICourseQueryCondition, data = {} as ICourseQueryCondition) {
  condition.keyword = data.keyword
  condition.certificationTypeName = data.certificationTypeName
  condition.certificationSubtypeName = data.certificationSubtypeName
  condition.requirement = data.requirement
  condition.pageNum = Number(data.pageNum)
  condition.kind = data.kind
  condition.pageSize = Number(data.pageSize)
}

export function resetCourseCondition (condition: ICourseQueryCondition, isMobile = false) {
  if (!isMobile) {
    condition.keyword = ''
  }
  condition.certificationTypeName = ''
  condition.certificationSubtypeName = ''
  condition.requirement = ''
}

export function getUrlParams (
  condition: Partial<ICourseQueryCondition | IVideoQueryCondition>,
  isMobile = false,
  type = 'course'
): Partial<ICourseQueryCondition | IVideoQueryCondition> {
  const defaultCondition = type === 'course' ? createDefaultCondition(isMobile)
    : createVideoConditionReactive(isMobile)
  return {
    ...defaultCondition,
    ...condition
  }
}

export function useFilterHook () {
  const { t } = useI18n()
  const { state: courseState } = useStoreHooks()
  const listData = computed(() => courseState.course.list)

  const getRequirements = () => {
    const requirements = insertAllItem([
      { text: t('global.filter.compulsory'), id: '1' },
      { text: t('global.filter.elective'), id: '0' }
    ], 'text')
    return requirements
  }
  const certificationTypes = computed(() => [
    { typeName: t('global.filter.all'), id: '', subTypeName: [] as string[] },
    ...listData.value.certificationTypes
  ])
  const certificationSubTypes = computed(() => [
    t('global.filter.all'),
    ...listData.value.certificationSubTypes
  ])
  return {
    getRequirements,
    certificationTypes,
    certificationSubTypes
  }
}

export function useVideoFilterHook () {
  const { t } = useI18n()
  const { state: courseState } = useStoreHooks()
  const productLineFilters = [{
    name: t('global.filter.all'),
    value: ''
  }, ...courseState.course.categoryFilters.filter(i => i.type === '1').map(i => ({
    name: i.name,
    value: i.id
  }))]
  const videoTypeFilters = [{
    name: t('global.filter.all'),
    value: ''
  }, ...courseState.course.categoryFilters.filter(i => i.type === '2').map(i => ({
    name: i.name,
    value: i.id
  }))]
  const typeFilters = [
    { name: t('global.filter.all'), value: '0' },
    { name: t('course.filter.topic'), value: '1' },
    { name: t('course.filter.course'), value: '2' }
  ]
  return {
    productLineFilters,
    videoTypeFilters,
    typeFilters
  }
}
